/**
 * Voice HUD — composed gap surface (SDD §7.3, OD-2: "built from the package's design language; ⌘K composer shell +
 * Journal entry card + header pill grammar"). No reference markup exists for voice, so this is composed, excluded from
 * the reference visual-regression set, and its baseline freezes at owner approval (gate G-1 / ESC-003).
 *
 * Renders: push-to-talk mic, the session state pill, the live transcript (partial dimmed), the barge-in cue, the
 * confirm-first prompt, the degrade banner and the minutes-exhausted state. Stacks directly above the reference's
 * composer/FAB column, which the transpiler marks with `data-composer-stack` (attribute only).
 */
import { useEffect, useState } from 'react';
import { Card, MicroLabel, SimulatedBadge, css } from '@pointfive/ui';
import { FAULT_LABEL, STATE_LABEL, STATE_TONE } from './machine';
import { pttKey, setPttKey, type VoiceApi } from './useVoice';

/** The capture waveform (ESC-003: "a responsive waveform" while listening). Deterministic bar heights — a fixed
 *  pattern breathing via CSS — so the same frame freezes identically under visreg and reduced-motion. */
function Waveform() {
  // Heights derive from the index — geometry, not data. (5..16px, scattered so neighbours differ.)
  const bars = Array.from({ length: 12 }, (_, i) => 5 + ((i * 7) % 12));
  return (
    <div aria-hidden="true" style={css('display:flex;align-items:center;gap:2px;height:18px')}>
      {bars.map((h, i) => (
        <span
          key={i}
          className="pf-wave-bar"
          style={css(
            `width:2.5px;border-radius:2px;background:var(--pos);height:${h}px;animation:pfwave 1.1s ease-in-out ${i * 0.09}s infinite alternate`,
          )}
        ></span>
      ))}
    </div>
  );
}

/** 'Space' → 'space', 'KeyV' → 'V' — the label a human reads on the affordance. */
function keyLabel(code: string): string {
  return code.startsWith('Key') ? code.slice(3) : code.toLowerCase();
}

export function VoiceHud({ voice }: { voice: VoiceApi }) {
  const { ctx } = voice;
  const [bottom, setBottom] = useState(90);
  const [pttCode, setPttCode] = useState(pttKey());
  const [capturingKey, setCapturingKey] = useState(false);
  // Reassignment captures exactly one keydown, whole-window, then stands down — Escape cancels.
  useEffect(() => {
    if (!capturingKey) return;
    const grab = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code !== 'Escape') {
        setPttKey(e.code);
        setPttCode(e.code);
      }
      setCapturingKey(false);
    };
    window.addEventListener('keydown', grab, { capture: true });
    return () => window.removeEventListener('keydown', grab, { capture: true });
  }, [capturingKey]);
  // stack above the reference composer column without touching its markup
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector<HTMLElement>('[data-composer-stack]');
      setBottom(el ? window.innerHeight - el.getBoundingClientRect().top + 12 : 90);
    };
    measure();
    const ro = new ResizeObserver(measure);
    const el = document.querySelector<HTMLElement>('[data-composer-stack]');
    if (el) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  });
  if (!voice.active) return null;
  const tone = STATE_TONE[ctx.state];
  return (
    <div
      data-voice-hud
      style={css(
        `position:fixed;bottom:${bottom}px;right:22px;z-index:61;width:360px;max-width:calc(100vw - 44px);display:flex;flex-direction:column;gap:10px;align-items:flex-end`,
      )}
    >
      <Card padding="none" extra="width:100%;box-shadow:var(--shadow-float);overflow:hidden">
        <div
          style={css(
            'display:flex;align-items:center;gap:9px;padding:11px 14px;border-bottom:1px solid var(--l7)',
          )}
        >
          <span
            aria-hidden="true"
            style={css(
              `width:7px;height:7px;border-radius:50%;background:${tone};flex:none${ctx.state === 'listening' ? ';animation:pfpulse 1.4s ease-in-out infinite' : ''}`,
            )}
          ></span>
          <span
            role="status"
            aria-live="polite"
            style={css(
              `font-size:9.5px;letter-spacing:0.13em;text-transform:uppercase;font-weight:600;color:${tone}`,
            )}
          >
            {STATE_LABEL[ctx.state]}
          </span>
          <div style={css('flex:1')} />
          <SimulatedBadge what="voice" compact />
          <button
            onClick={voice.end}
            aria-label="End voice session"
            style={css(
              'width:24px;height:24px;border:none;background:transparent;color:var(--muted2);cursor:pointer;border-radius:6px',
            )}
          >
            <i className="ph ph-x" style={css('font-size:13px')}></i>
          </button>
        </div>

        {ctx.fault ? (
          <div
            data-voice-fault={ctx.fault.kind}
            style={css(
              'display:flex;align-items:center;gap:8px;padding:9px 14px;border-bottom:1px solid var(--l7);background:color-mix(in srgb, var(--neg) 8%, transparent)',
            )}
          >
            <i className="ph ph-warning-octagon" style={css('font-size:13px;color:var(--neg);flex:none')}></i>
            <span style={css('font-size:11px;color:var(--text-mid);flex:1')}>
              <b>{FAULT_LABEL[ctx.fault.kind]}.</b> {ctx.fault.message}
            </span>
            <button
              onClick={() => voice.clearFault()}
              style={css(
                'font-size:10.5px;padding:4px 10px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
              )}
            >
              Try again
            </button>
          </div>
        ) : null}

        {ctx.degradeBanner ? (
          <div
            style={css(
              'display:flex;align-items:center;gap:8px;padding:9px 14px;border-bottom:1px solid var(--l7);background:color-mix(in srgb, var(--amb) 10%, transparent)',
            )}
          >
            <i className="ph ph-warning" style={css('font-size:13px;color:var(--amb-2);flex:none')}></i>
            <span style={css('font-size:11px;color:var(--text-mid)')}>{ctx.degradeBanner}</span>
          </div>
        ) : null}

        <div
          style={css(
            'padding:12px 14px;display:flex;flex-direction:column;gap:9px;max-height:280px;overflow-y:auto',
          )}
        >
          {ctx.state === 'listening' ? <Waveform /> : null}
          {ctx.transcript.length === 0 && !ctx.partial && ctx.state !== 'listening' ? (
            <div style={css('font-size:11.5px;color:var(--muted)')}>
              Hold the mic — or hold <b>{keyLabel(pttCode)}</b> — and ask about any metric, your rules, or
              dictate a journal entry.
            </div>
          ) : null}
          {ctx.transcript.map((line, i) => (
            <div key={i} style={css('display:flex;flex-direction:column;gap:3px')}>
              <MicroLabel>{line.role === 'user' ? 'You' : 'PointFive'}</MicroLabel>
              <div
                style={css(
                  `font-size:12.5px;line-height:1.55;color:${line.role === 'user' ? 'var(--text)' : 'var(--text2)'}`,
                )}
              >
                {line.text}
              </div>
            </div>
          ))}
          {ctx.partial ? (
            <div style={css('display:flex;flex-direction:column;gap:3px;opacity:0.55')}>
              <MicroLabel>You</MicroLabel>
              <div style={css('font-size:12.5px;line-height:1.55')}>{ctx.partial}…</div>
            </div>
          ) : null}
          {ctx.state === 'transcribing' ? (
            <div style={css('font-size:11px;color:var(--faint)')}>Finalising your words…</div>
          ) : null}
          {ctx.state === 'thinking' ? (
            <div style={css('font-size:11px;color:var(--faint)')}>Routing to a skill…</div>
          ) : null}
        </div>

        {ctx.confirmation ? (
          <div
            style={css(
              'display:flex;align-items:center;gap:9px;padding:11px 14px;border-top:1px solid var(--l7);background:var(--l3)',
            )}
          >
            <span style={css('font-size:11.5px;color:var(--text-mid);flex:1')}>
              Confirm <b>{ctx.confirmation.intent.replace('.', ' ')}</b> on {ctx.confirmation.object}?
            </span>
            <button
              onClick={() => void voice.confirm()}
              style={css(
                'font-size:11.5px;padding:6px 13px;border-radius:99px;border:1px solid color-mix(in srgb, var(--pos) 45%, transparent);background:color-mix(in srgb, var(--pos) 10%, transparent);color:var(--pos);cursor:pointer',
              )}
            >
              Confirm
            </button>
          </div>
        ) : null}

        <div
          style={css(
            'display:flex;align-items:center;gap:10px;padding:11px 14px;border-top:1px solid var(--l7)',
          )}
        >
          <button
            onPointerDown={voice.press}
            onPointerUp={voice.release}
            onPointerLeave={voice.release}
            aria-label={ctx.state === 'speaking' ? 'Interrupt and speak (barge-in)' : 'Hold to talk'}
            aria-pressed={ctx.state === 'listening'}
            style={css(
              `display:inline-flex;align-items:center;gap:7px;font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid ${ctx.state === 'listening' ? 'color-mix(in srgb, var(--pos) 50%, transparent)' : 'var(--l10)'};background:${ctx.state === 'listening' ? 'color-mix(in srgb, var(--pos) 10%, transparent)' : 'transparent'};color:${ctx.state === 'listening' ? 'var(--pos)' : 'var(--text-mid)'};cursor:pointer`,
            )}
          >
            <i
              className={`ph ${ctx.state === 'speaking' ? 'ph-hand-palm' : 'ph-microphone'}`}
              style={css('font-size:13px')}
            ></i>
            {ctx.state === 'speaking' ? 'Interrupt' : 'Hold to talk'}
          </button>
          {ctx.bargeInAvailable ? (
            <span style={css('font-size:10.5px;color:var(--faint)')}>Barge-in keeps the thread</span>
          ) : null}
          <div style={css('flex:1')} />
          <button
            onClick={() => setCapturingKey(true)}
            aria-label={`Push-to-talk key: ${keyLabel(pttCode)}. Press to reassign.`}
            style={css(
              `font-size:10px;padding:3px 8px;border-radius:6px;border:1px dashed var(--l10);background:${capturingKey ? 'var(--l3)' : 'transparent'};color:var(--faint);cursor:pointer`,
            )}
          >
            {capturingKey ? 'press a key…' : `key: ${keyLabel(pttCode)}`}
          </button>
          <span style={css('font-size:10px;color:var(--faint);font-variant-numeric:tabular-nums')}>
            {ctx.minutes.used}/{ctx.minutes.quota} min
          </span>
        </div>
      </Card>
    </div>
  );
}
